import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type {
	GridCustomCellProps,
	GridCustomHeaderCellProps,
} from "@tql/ui-components-react/kendo/grid";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { file_06 } from "@tql/ui-components-react/untitled/icons";

import { LocalDateTextFilterCell } from "~/shared/components/trax-grid/date-text-filter-cell/date-text-filter-cell.tsx";
import { GridTd } from "~/shared/components/trax-grid/grid-td/grid-td.tsx";
import { InvoiceStatusCell } from "~/shared/components/trax-grid/invoice-status-cell/invoice-status-cell.tsx";
import { NoDataCell } from "~/shared/components/trax-grid/no-data-cell/no-data-cell.tsx";
import { SelectCell } from "~/shared/components/trax-grid/select-cell/select-cell.tsx";
import { SelectCellHeader } from "~/shared/components/trax-grid/select-cell-header/select-cell-header.tsx";
import type { Invoice } from "~/shared/schemas/invoice-schema";
import {
	formatCurrency,
	formatDateToMMDDYYYY,
} from "~/shared/utils/string-fns.ts";

type GetPaymentColumnsParams = {
	handleDeselect: (id: number | string) => void;
	handleSelect: (id: number | string, selectedByAction?: boolean) => void;
	selectRecord: Record<string | number, boolean>;
	isAllSelected: boolean;
	handleSelectAll: (selected: boolean) => void;
	handleDocumentClick: (invoice: Invoice) => void;
};

export const getPaymentColumns = ({
	handleDeselect,
	handleSelect,
	selectRecord,
	isAllSelected,
	handleSelectAll,
	handleDocumentClick,
}: GetPaymentColumnsParams) => [
	{
		field: "",
		title: "",
		menu: false,
		show: true,
		filterable: false,
		reorderable: false,
		width: 40,

		cells: {
			data: (props: GridCustomCellProps) => (
				<SelectCell
					{...props}
					onDeselect={handleDeselect}
					onSelect={handleSelect}
					select={selectRecord}
				/>
			),
			headerCell: (props: GridCustomHeaderCellProps) => (
				<SelectCellHeader
					{...props}
					allDataSelected={isAllSelected}
					onSelectChange={handleSelectAll}
					setAllDataSelected={() => {}}
				/>
			),
		},
	},
	{
		field: "docs",
		title: "Docs",
		menu: false,
		show: true,
		required: true,
		reorderable: false,
		filterable: false,
		resizable: false,
		width: 50,
		cells: {
			data: (props: GridCustomCellProps) => (
				<GridTd
					style={{
						...props.tdProps?.style,
						textAlign: "center",
						verticalAlign: "middle",
					}}
					tdProps={props.tdProps}
				>
					<Button
						aria-label={"Documents"}
						onClick={() => {
							handleSelect((props.dataItem as Invoice).invoiceID, true);
							handleDocumentClick(props.dataItem as Invoice);
						}}
						startIcon={<SvgIcon icon={file_06} size={"large"} />}
						title={"Documents"}
						variant={"tertiary"}
					/>
				</GridTd>
			),
		},
	},
	{
		field: "invoiceStatus",
		title: "Status",
		menu: true,
		show: true,
		required: true,
		reorderable: false,
		// Pinned (required) columns bypass `applyGridPrefs`, so width must
		// be declared inline here.
		width: 110,
		cells: {
			data: (props: GridCustomCellProps) => <InvoiceStatusCell {...props} />,
		},
	},
	{
		field: "poNumber",
		title: "TQL PO #",
		menu: true,
		show: true,
		required: true,
		reorderable: false,
		// Pinned (required) columns bypass `applyGridPrefs`, so width must
		// be declared inline here.
		width: 120,
	},
	{
		field: "custPO",
		title: "Customer PO #",
		menu: true,
		show: true,
	},
	{
		field: "balanceAmt",
		title: "Balance",
		menu: true,
		show: true,
		cells: {
			data: (props: GridCustomCellProps) => {
				const invoice = props.dataItem as Invoice;
				return (
					<GridTd tdProps={props.tdProps}>
						{formatCurrency(invoice.balanceAmt)}
					</GridTd>
				);
			},
		},
	},
	{
		field: "paidAmt",
		title: "Paid",
		menu: true,
		show: true,
		cells: {
			data: (props: GridCustomCellProps) => {
				const invoice = props.dataItem as Invoice;
				return (
					<GridTd tdProps={props.tdProps}>
						{formatCurrency(invoice.paidAmt)}
					</GridTd>
				);
			},
		},
	},
	{
		field: "invoiceAmt",
		title: "Rate",
		menu: true,
		show: true,
		cells: {
			data: (props: GridCustomCellProps) => {
				const invoice = props.dataItem as Invoice;
				return (
					<GridTd tdProps={props.tdProps}>
						{formatCurrency(invoice?.invoiceAmt)}
					</GridTd>
				);
			},
		},
	},
	{
		field: "dueDate",
		title: "Due Date",
		menu: true,
		show: true,
		filterCell: LocalDateTextFilterCell,
		cells: {
			data: (props: GridCustomCellProps) => {
				const invoice = props.dataItem as Invoice;
				const dueDate = invoice.dueDate
					? formatDateToMMDDYYYY(invoice.dueDate)
					: null;
				return <NoDataCell {...props} value={dueDate} />;
			},
		},
	},
	{
		field: "docdate",
		title: "Invoice Date",
		menu: true,
		show: true,
		filterCell: LocalDateTextFilterCell,
		cells: {
			data: (props: GridCustomCellProps) => {
				const invoice = props.dataItem as Invoice;
				const docDate = invoice.docdate
					? formatDateToMMDDYYYY(invoice.docdate)
					: null;
				return <NoDataCell {...props} value={docDate} />;
			},
		},
	},
	{
		field: "age",
		title: "Age (Days)",
		menu: true,
		show: true,
		cells: {
			data: (props: GridCustomCellProps) => {
				const invoice = props.dataItem as Invoice;
				const age = invoice.age;
				return <NoDataCell {...props} value={age} />;
			},
		},
	},
	{
		field: "deliveryDate",
		title: "Delivery Date",
		menu: true,
		show: true,
		filterCell: LocalDateTextFilterCell,
		cells: {
			data: (props: GridCustomCellProps) => {
				const invoice = props.dataItem as Invoice;
				const deliveryDate = invoice.deliveryDate
					? formatDateToMMDDYYYY(invoice.deliveryDate)
					: null;
				return <NoDataCell {...props} value={deliveryDate} />;
			},
		},
	},
	{
		field: "mode",
		title: "Mode",
		menu: true,
		show: false,
	},
	{
		field: "firstPickupState",
		title: "Origin",
		menu: true,
		show: false,
	},
	{
		field: "lastDropState",
		title: "Destination",
		menu: true,
		show: false,
	},
];

export type PaymentExportColumn = {
	field: string;
	title: string;
	defaultShow: boolean;
};

export const PAYMENT_EXPORT_COLUMNS: PaymentExportColumn[] = [
	{ field: "invoiceStatus", title: "Status", defaultShow: true },
	{ field: "poNumber", title: "TQL PO Number", defaultShow: true },
	{ field: "custPO", title: "Customer PO Number", defaultShow: true },
	{ field: "balanceAmt", title: "Balance", defaultShow: true },
	{ field: "paidAmt", title: "Paid", defaultShow: true },
	{ field: "invoiceAmt", title: "Rate", defaultShow: true },
	{ field: "dueDate", title: "Due Date", defaultShow: true },
	{ field: "docdate", title: "Invoice Date", defaultShow: true },
	{ field: "age", title: "Age (Days)", defaultShow: true },
	{ field: "deliveryDate", title: "Delivery Date", defaultShow: true },
	{ field: "mode", title: "Mode", defaultShow: false },
	{ field: "firstPickupState", title: "Origin", defaultShow: false },
	{ field: "lastDropState", title: "Destination", defaultShow: false },
];
